import { jsx, jsxs } from 'react/jsx-runtime';
import { isScrollable, canScrollTo } from '@flex-design-system/fx-utils/element';
import { useResizeObserver } from '@flex-design-system/fx-utils/hooks';
import { styled } from '@flex-design-system/stitches-react';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { Primitive } from '@radix-ui/react-primitive';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import classNames from 'classnames';
import { forwardRef, useState, useCallback } from 'react';
import { ScrollArea } from '../../../scroll-area/ScrollArea.mjs';
import { FOG_HEIGHT } from '../../styles.vanilla.mjs';
import { ModalFog } from '../fog/Fog.mjs';
import { asideRoot, asideRootHeight, scrollAreaViewPort, scrollAreaViewPortPaddingBottom, scrollAreaRoot, scrollAreaChildrenRoot, modalFog, asideFooter } from './aside.vanilla.mjs';

const ModalAside = forwardRef(
  ({
    children,
    height,
    side,
    onScroll,
    className: classNameFromProps,
    style: styleFromProps,
    ...props
  }, ref) => {
    const className = classNames(asideRoot({ side }), classNameFromProps);
    const style = Object.assign(
      {},
      assignInlineVars({
        [asideRootHeight]: typeof height === "number" ? `${height}px` : height
      }),
      styleFromProps
    );
    return /* @__PURE__ */ jsx(StyledAside, { className, style, ref, ...props, children });
  }
);
const ModalAsideScrollArea = forwardRef(
  ({
    children,
    height,
    footer,
    side,
    className: classNameFromProps,
    style: styleFromProps,
    onScroll,
    bottomFog,
    ...props
  }, ref) => {
    const [viewportElement, setViewportElement] = useState(null);
    const [needScrollFog, setNeedScrollFog] = useState(false);
    const composedViewportRef = useComposedRefs(
      ref,
      (node) => setViewportElement(node)
    );
    const scrollable = isScrollable(viewportElement);
    const isFogDisabled = bottomFog === false;
    const isFogDisplayed = !isFogDisabled && needScrollFog;
    const className = classNames(scrollAreaViewPort, classNameFromProps);
    const style = Object.assign(
      {},
      assignInlineVars({
        [scrollAreaViewPortPaddingBottom]: `${scrollable ? FOG_HEIGHT : 64}px`
      }),
      styleFromProps
    );
    const handleScroll = useCallback(() => {
      setNeedScrollFog(canScrollTo(viewportElement, "bottom"));
    }, [viewportElement]);
    useResizeObserver(viewportElement, handleScroll);
    return /* @__PURE__ */ jsx(Primitive.div, { className: scrollAreaRoot, children: /* @__PURE__ */ jsxs(ScrollArea, { children: [
      /* @__PURE__ */ jsx(
        ScrollArea.Viewport_Legacy,
        {
          className: classNames(scrollAreaViewPort, className),
          style,
          onScroll: composeEventHandlers(onScroll, handleScroll),
          ref: composedViewportRef,
          ...props,
          children: /* @__PURE__ */ jsx("div", { className: scrollAreaChildrenRoot, children })
        }
      ),
      isFogDisplayed ? bottomFog ?? /* @__PURE__ */ jsx(ModalFog, { side: "bottom", color: "#FFFFFF", className: modalFog }) : null
    ] }) });
  }
);
const ModalAsideFooter = forwardRef(({ className: classNameFromProps, ...props }, ref) => {
  const className = classNames(asideFooter, classNameFromProps);
  return /* @__PURE__ */ jsx(
    StyledFooter,
    {
      "data-scope": "modal",
      "data-part": "aside",
      className,
      ref,
      ...props
    }
  );
});
ModalAsideFooter.toString = () => '[data-scope="modal"][data-part="aside"]';
const StyledAside = styled("aside");
const StyledFooter = styled("footer");

export { ModalAside, ModalAsideFooter, ModalAsideScrollArea };
//# sourceMappingURL=Aside.mjs.map
